/**
 * Channel reconciliation — `/channels/reconciliation`.
 *
 * A reconciliation report is a proof that was run: it publishes both sides —
 * what the channel counted and what the ERP counted — next to the difference,
 * so a reader can redo the subtraction instead of trusting it. This screen
 * shows all three for that reason, and never recomputes a delta itself.
 *
 * A pending report has no figures yet, and an errored one has a stage and a
 * code instead of figures. Neither is rendered as zero.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, textCell } from "@/components/data-table";
import { MoneyDisplay } from "@/components/money/money-display";
import { formatCalendarDate } from "@/lib/format";

import {
  RECONCILIATION_STATUSES,
  type ReconciliationFilters,
  type ReconciliationReport,
  type ReconciliationStatus,
} from "../api";
import { useChannels, useReconciliationReports } from "../queries";

const ALL = "__all__";

const STATUS_VARIANTS: Record<ReconciliationStatus, "outline" | "success" | "destructive"> = {
  pending: "outline",
  matched: "success",
  discrepancy: "destructive",
  error: "destructive",
};

export interface ReconciliationPageProps {
  pageLimit?: number;
}

export function ReconciliationPage({ pageLimit }: ReconciliationPageProps) {
  const { t, i18n } = useTranslation();
  const channels = useChannels();
  const [channelId, setChannelId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>(ALL);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");

  const rows = channels.data ?? [];
  const activeChannelId = channelId ?? rows[0]?.id;

  const filters = useMemo<ReconciliationFilters>(() => {
    const built: ReconciliationFilters = {};
    if (status !== ALL) built.status = status as ReconciliationStatus;
    if (periodFrom !== "") built.period_from = new Date(periodFrom).toISOString();
    if (periodTo !== "") built.period_to = new Date(periodTo).toISOString();
    return built;
  }, [status, periodFrom, periodTo]);

  const query = useReconciliationReports(activeChannelId, filters, pageLimit);

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const columns = useMemo<ColumnDef<ReconciliationReport, unknown>[]>(
    () => [
      {
        id: "period",
        accessorKey: "period_start",
        header: t("channels.reconciliation.columns.period", "Period"),
        cell: ({ row }) =>
          textCell(`${day(row.original.period_start)} – ${day(row.original.period_end)}`),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("channels.reconciliation.columns.status", "Result"),
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANTS[row.original.status]}>
            {t(`channels.reconciliationStatus.${row.original.status}`, row.original.status)}
          </Badge>
        ),
      },
      {
        id: "orders",
        accessorKey: "channel_order_count",
        header: t("channels.reconciliation.columns.orders", "Orders (channel / ERP)"),
        meta: { numeric: true },
        cell: ({ row }) =>
          row.original.channel_order_count === undefined ||
          row.original.erp_order_count === undefined
            ? textCell("—")
            : textCell(`${row.original.channel_order_count} / ${row.original.erp_order_count}`),
      },
      {
        id: "order_count_delta",
        accessorKey: "order_count_delta",
        header: t("channels.reconciliation.columns.orderDelta", "Order difference"),
        meta: { numeric: true },
        cell: ({ row }) =>
          row.original.order_count_delta === undefined
            ? textCell("—")
            : textCell(String(row.original.order_count_delta)),
      },
      {
        id: "channel_grand_total",
        accessorKey: "channel_grand_total",
        header: t("channels.reconciliation.columns.channelTotal", "Channel total"),
        meta: { numeric: true },
        cell: ({ row }) =>
          row.original.channel_grand_total === undefined ? (
            textCell("—")
          ) : (
            <MoneyDisplay value={row.original.channel_grand_total} />
          ),
      },
      {
        id: "erp_grand_total",
        accessorKey: "erp_grand_total",
        header: t("channels.reconciliation.columns.erpTotal", "ERP total"),
        meta: { numeric: true },
        cell: ({ row }) =>
          row.original.erp_grand_total === undefined ? (
            textCell("—")
          ) : (
            <MoneyDisplay value={row.original.erp_grand_total} />
          ),
      },
      {
        id: "grand_total_delta",
        accessorKey: "grand_total_delta",
        header: t("channels.reconciliation.columns.totalDelta", "Difference"),
        meta: { numeric: true },
        cell: ({ row }) =>
          row.original.grand_total_delta === undefined ? (
            textCell("—")
          ) : (
            <MoneyDisplay value={row.original.grand_total_delta} />
          ),
      },
      {
        id: "error_code",
        accessorKey: "error_code",
        header: t("channels.reconciliation.columns.failure", "Failure"),
        cell: ({ row }) =>
          row.original.error_code === undefined
            ? textCell("—")
            : textCell(
                `${t(`channels.reconciliationStage.${row.original.error_stage ?? "fetch"}`, row.original.error_stage ?? "")} · ${row.original.error_code}`,
              ),
      },
    ],
    // `day` closes over the active locale, which `t` already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("channels.reconciliation.title", "Channel reconciliation")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "channels.reconciliation.subtitle",
            "What the channel counted, what we counted, and the difference between them.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="recon-channel">{t("channels.reconciliation.channel", "Channel")}</Label>
          <Select value={activeChannelId ?? ""} onValueChange={setChannelId}>
            <SelectTrigger id="recon-channel" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rows.map((channel) => (
                <SelectItem key={channel.id} value={channel.id}>
                  {channel.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="recon-status">
            {t("channels.reconciliation.columns.status", "Result")}
          </Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="recon-status" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {RECONCILIATION_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`channels.reconciliationStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="recon-from">{t("channels.reconciliation.from", "From")}</Label>
          <Input
            id="recon-from"
            type="date"
            className="w-40"
            value={periodFrom}
            onChange={(event) => setPeriodFrom(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="recon-to">{t("channels.reconciliation.to", "To")}</Label>
          <Input
            id="recon-to"
            type="date"
            className="w-40"
            value={periodTo}
            onChange={(event) => setPeriodTo(event.target.value)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        emptyMessage={t("channels.reconciliation.empty", "No reconciliation runs for this period.")}
        storageKey="channels.reconciliation.columns"
      />
    </div>
  );
}
